import { reduxKey } from './constants';
import { PapiDeviation, PapiRequestDreamsofartGenerate } from '@wix/da-papi-types';

export interface IngressFormPersistentValues {
  prompt: string;
  artStyle: ArtStyle;
}

/**
 * We submit prompts to the server for approval. This enum helps us keep track of a submission network request's current status.
 */
export enum SubmissionStatus {
  Idle = 'idle',
  AwaitingResponse = 'awaiting-response',
  Error = 'error',
}

/**
 * Prompt submissions could be rejected or outright fail for a number of reasons.
 */
export enum SubmissionError {
  Unknown = 'unknown',
  ForbiddenTermsInPrompt = 'forbidden-terms',
  NotEnoughStashSpace = 'not-enough-stash-space',
  NotEnoughPoints = 'not-enough-points',
  ValidationError = 'validation-error',
}

export enum ArtStyle {
  Default = 'default',
  Fantasy = 'fantasy',
  Anime = 'anime',
  Anthro = 'anthro',
  Photographic = 'photographic',
  Vaporwave = 'vaporwave',
  Lowpoly = 'lowpoly',
  Origami = 'origami',
  LineArt = 'line_art',
  CraftClay = 'craft_clay',
  Cinematic = 'cinematic',
  ThreeDModel = '3d_model',
  PixelArt = 'pixel_art',
}

/**
 * From whence a user selected a deviation for use as a variation target.
 */
export enum VariationSource {
  Dreamup = 'dreamup',
  MediaSelector = 'media-selector',
}

export interface FormStoreSlice {
  formValues: {
    /**
     * The prompt the user has entered. This is stored in redux because we have to interact with it in a variety of
     * places.
     */
    prompt: string;

    /**
     * Used for the generation of image variations. How similar to the original image that the result will be.
     */
    similarity: number;

    /**
     * What dimensions should the resulting images be?
     *
     * NonNullable because the UI has a default set and users can't unselect the option
     * entirely.
     */
    aspectRatio: NonNullable<PapiRequestDreamsofartGenerate['aspect_ratio']>;

    /**
     * I'm not sure exactly what this means, but it probably makes the prompt have more of an effect when paired with
     * the art style input or a variation target? Anyway, meant to be between 0 and 20.
     */
    promptStrength: number;

    /**
     * A string that maps to an art style. Abstract, anime, vaporwave?
     */
    artStyle: ArtStyle;

    /**
     * Whether our negative prompt input is visible.
     */
    isNegativePromptInputVisible: boolean;

    /**
     * The negative prompt the user has entered. This is stored in redux because we have to interact with it in a variety of
     * places.
     */
    negativePrompt: string;
  };

  isPromptSettingsPanelVisible: boolean;

  /**
   * If `true`, we'll try to pull some form values stored within local storage under the key {@link CompliantLocalStorageKeys.DREAMUP_ANON_FORM}.
   */
  shouldPullFormValuesFromLocalStorage: boolean;

  /**
   * If the user has an image selected as input for our AI, this'll be it.
   */
  variationTarget?: PapiDeviation;

  /**
   * From whence a user selected a deviation for use as a variation target. There are different UI interactions with the
   * prompt depending on this. e.g. if the source is dreamup, we clear the prompt if we remove the variation target
   */
  variationSource?: VariationSource;

  /**
   * When a user clicks on the create button, the server will verify the prompt doesn't contain
   * forbidden terms. We need to update the UI while we wait or when errors are returned.
   *
   * e.g. disabling the create button while the request is pending.
   */
  currentSubmission: {
    status: SubmissionStatus;
    error?: SubmissionError;
    /**
     * Some error types returned by the server can apply to a wide variety of circumstances. In those cases,
     * we'll pass the server error message straight through instead of relying on stuff in babel.
     */
    serverErrorMessage?: string;
  };

  /**
   * We display purchase links for points in a variety of amounts. These are those
   * points packages.
   *
   * A server-side value included in redux for ease of use.
   */
  pointsPackages: {
    /**
     * The number of points a user will receive if they purchase this package.
     */
    points: number;

    /**
     * The price of the package in dollars.
     */
    price: number;

    /**
     * The product id of the package that we'll need to pass to checkout.
     */
    subproductId: number;
  }[];

  /**
   * A variety of restrictions that apply to the user's ability to submit prompts. These values are only updated
   * based on server responses, no UI interactions can update them directly.
   */
  restrictions: {
    /**
     * A server-side value included in redux for ease of use.
     */
    canUseFreePrompt: boolean;

    /**
     * A server-side value included in redux for ease of use.
     */
    canUsePaidPrompt: boolean;

    /**
     * The maximum number of free prompts we grant to the user's current core level.
     *
     * A server-side value included in redux for ease of use.
     */
    freePromptsAllowed: number;

    /**
     * The number of free prompts the user has left.
     *
     * A server-side value included in redux for ease of use.
     */
    freePromptsRemaining: number;

    /**
     * Each core level generates a different number of images per prompt. This value reflects that.
     *
     * A server-side value included in redux for ease of use.
     */
    imagesPerPrompt: number;

    /**
     * Each core level has a different price for paid prompts. This value reflects that.
     *
     * A server-side value included in redux for ease of use.
     */
    promptPointsPrice: number;

    /**
     * Some core levels have higher queue priority than others. This value reflects that.
     *
     * A server-side value included in redux for ease of use.
     */
    queuePriority: 'low' | 'high';
  };

  /**
   * The MediaPopup for picking a starting image is detached from the button which shows it.
   */
  isStartingImageSelectorOpen: boolean;
}

export interface FormStoreState {
  [reduxKey]: FormStoreSlice;
}
